import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { MasterLocaleSelects } from '@/components/master/MasterLocaleSelects';
import { Input } from '@/components/ui/Input';
import { storesApi, customersApi, productsApi } from '@/api';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';
import { setOnboardingDone } from './onboardingState';

const STEPS = ['Store', 'Product', 'Customer', 'Open POS'] as const;

export function PosOnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [store, setStore] = useState({
    name: '',
    code: '',
    countryCode: 'IN',
    currencyCode: 'INR',
    timezone: 'Asia/Kolkata',
  });
  const [product, setProduct] = useState({ name: '', sku: '', price: '' });
  const [customer, setCustomer] = useState({ name: '', phone: '' });
  const [busy, setBusy] = useState(false);

  const finish = () => {
    setOnboardingDone(true);
    navigate('/pos');
  };

  const createStore = async () => {
    if (!store.name.trim() || !store.code.trim()) {
      notify.error('Store name and code are required');
      return;
    }
    setBusy(true);
    try {
      await storesApi.create({
        name: store.name.trim(),
        code: store.code.trim(),
        countryCode: store.countryCode,
        currencyCode: store.currencyCode,
        timezone: store.timezone,
        taxMode: 'GST',
        taxInclusive: true,
      });
      notify.success('Store created');
      setStep(1);
    } catch (err) {
      showApiError(err, 'Failed to create store');
    } finally {
      setBusy(false);
    }
  };

  const createProduct = async () => {
    if (!product.name.trim()) {
      notify.error('Product name is required');
      return;
    }
    setBusy(true);
    try {
      await productsApi.create({
        name: product.name.trim(),
        sku: product.sku.trim() || `SKU-${Date.now()}`,
        sellingPrice: product.price ? Number(product.price) : 0,
      });
      notify.success('Product created');
      setStep(2);
    } catch (err) {
      showApiError(err, 'Failed to create product');
    } finally {
      setBusy(false);
    }
  };

  const createCustomer = async () => {
    if (!customer.name.trim()) {
      notify.error('Customer name is required');
      return;
    }
    setBusy(true);
    try {
      await customersApi.create({
        name: customer.name.trim(),
        phone: customer.phone.trim() || undefined,
      });
      notify.success('Customer created');
      setStep(3);
    } catch (err) {
      showApiError(err, 'Failed to create customer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <PageHeader
        title="Set up Point of Sale"
        description="Complete these steps to start selling"
      />
      <div className="mb-6 flex gap-2">
        {STEPS.map((label, i) => (
          <span
            key={label}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              i === step
                ? 'bg-primary-100 text-primary-800 dark:bg-primary-950 dark:text-primary-200'
                : i < step
                  ? 'bg-slate-100 text-slate-600 dark:bg-slate-800'
                  : 'bg-slate-50 text-slate-400 dark:bg-slate-900'
            }`}
          >
            {label}
          </span>
        ))}
      </div>
      <div className="card space-y-4 p-6">
        {step === 0 && (
          <>
            <Input label="Store name" value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })} />
            <Input label="Store code" value={store.code} onChange={(e) => setStore({ ...store, code: e.target.value })} />
            <MasterLocaleSelects
              value={{
                countryCode: store.countryCode,
                currencyCode: store.currencyCode,
                timezone: store.timezone,
              }}
              onChange={(patch) => setStore((s) => ({ ...s, ...patch }))}
              countryRequired
              currencyRequired
              timezoneRequired
            />
            <Button onClick={() => void createStore()} loading={busy}>
              Save & continue
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Input label="Product name" value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} />
            <Input label="SKU" value={product.sku} onChange={(e) => setProduct({ ...product, sku: e.target.value })} />
            <Input label="Price" type="number" value={product.price} onChange={(e) => setProduct({ ...product, price: e.target.value })} />
            <Button onClick={() => void createProduct()} loading={busy}>
              Save & continue
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Input label="Customer name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
            <Input label="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
            <Button onClick={() => void createCustomer()} loading={busy}>
              Save & continue
            </Button>
          </>
        )}
        {step === 3 && (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              You are ready to open the POS terminal and record your first sale.
            </p>
            <Button onClick={finish}>Open POS terminal</Button>
            <Button variant="ghost" onClick={() => { setOnboardingDone(true); navigate('/org'); }}>
              Go to dashboard instead
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
